import { ICreditSeed } from '@/types/domain/credit'

interface FincodeTokenResponse {
  card_no?: string
  expire?: string
  security_code_set?: string
  list?: Array<{
    token: string
  }>
  errors?: Array<{
    error_code: string
    error_message: string
  }>
}

// Fincode SDKの型定義
interface FincodeInstance {
  tokens: (
    card: {
      card_no: string
      expire: string
      security_code: string
      holder_name: string
      number?: string
    },
    successCallback: (status: number, response: FincodeTokenResponse) => void,
    errorCallback: () => void
  ) => void
}

declare global {
  interface Window {
    Fincode: (apiKey: string) => FincodeInstance
  }
}

export default class FincodeGateway {
  getCreditToken(
    seed: ICreditSeed
  ): Promise<{
      token: string
      token_expire_date: string
      req_card_number: string
      status: 'success'
      code: string
      message: string
    }> {
    // 環境変数からFincode APIキー（public key）を取得
    // CodeBuildでAWS Secrets Managerから環境変数として設定される
    const apiKey = process.env.FINCODE_API_PUBLIC_KEY as string

    if (!apiKey) {
      throw new Error('Fincode API public key is not configured')
    }

    // Fincode SDKが読み込まれているか確認
    if (typeof window === 'undefined' || !window.Fincode) {
      throw new Error('Fincode SDK is not loaded')
    }

    const { cardNumber, cardExpYear, cardExpMonth, securityCode, holderName } = seed
    // YYMM形式に変換（例: 2025年12月 → "2512"）
    const expire = String(cardExpYear).slice(2) + ('0' + String(cardExpMonth)).slice(-2)

    // Fincode インスタンス初期化
    const fincode = window.Fincode(apiKey)

    // Promise形式でラップしてコールバック形式のAPIを使用
    return new Promise<{
      token: string
      token_expire_date: string
      req_card_number: string
      status: 'success'
      code: string
      message: string
    }>((resolve, reject) => {
      fincode.tokens(
        {
          card_no: cardNumber,
          expire,
          security_code: securityCode,
          holder_name: holderName,
          number: '1'
        },
        (status: number, response: FincodeTokenResponse) => {
          // 成功時の処理
          if (status === 200 && response.list && response.list.length > 0) {
            const tokenData = response.list[0]
            resolve({
              token: tokenData.token,
              token_expire_date: response.expire || '',
              req_card_number: response.card_no || '',
              status: 'success',
              code: 'success',
              message: 'Token has been successfully created.'
            })
          } else {
            // エラー時の処理
            let errorMessage = 'Token generation failed'
            if (response.errors && response.errors.length > 0) {
              const error = response.errors[0]
              errorMessage = error.error_message + ' (' + error.error_code + ')'
            }
            reject(new Error(errorMessage))
          }
        },
        () => {
          // 通信エラー時の処理
          reject(new Error('通信エラーが発生しました。しばらく経ってから再度お試しください。'))
        }
      )
    })
  }
}

export const FincodeGatewayFactory = (): FincodeGateway => {
  return new FincodeGateway()
}
